import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  Close as CloseIcon,
  Assessment as ReportIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import "leaflet/dist/leaflet.css";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
} from "@mui/x-data-grid";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Notification from "../components/Notification";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";
import TripPathMap from "../components/TripPathMap";

// API types from backend /admin/trips
interface ApiTripDriver {
  id: string;
  name: string;
  email: string;
}

interface ApiTrip {
  id: string;
  serviceDate: string; // ISO date
  startTime: string; // ISO string
  startLocation: string;
  endTime?: string | null;
  endLocation?: string | null;
  state: "ongoing" | "completed" | "cancelled" | string;
  driver: ApiTripDriver;
}
// Trip details API types (for GET /trips/:tripId)
interface ApiUserMinimal {
  id: string;
  name: string;
  email: string;
}

interface ApiUserTripDetail {
  user: ApiUserMinimal;
  status: "joined" | "completed" | "cancelled" | string;
  boardingLocation?: string | null;
  alightingLocation?: string | null;
  boardingTime?: string | null;
  alightingTime?: string | null;
}

interface ApiTripDetails extends ApiTrip {
  userTrips: ApiUserTripDetail[];
  locationLogs?: { location: string; timestamp: string }[];
}

interface ApiTripDetailsResponse {
  success: boolean;
  trip?: ApiTripDetails;
  error?: string;
}
interface ApiLocationLog { location: string; timestamp: string }
interface ApiLocationLogsResponse { success: boolean; logs: ApiLocationLog[] }
// UI row model (flattened for simpler rendering)
interface UITripRow {
  id: string;
  state: string;
  // display fields
  serviceDateText: string;
  driverName: string;
  startLocation: string;
  endLocationText: string;
  distanceKmText: string; // e.g., "5.23" or "-"
  startTimeText: string;
  endTimeText: string;
  durationMinsText: string; // e.g., "12" or "-"
  // raw coords preserved for mapping
  rawStartLocation?: string | null;
  rawEndLocation?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const Trips: React.FC = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rows, setRows] = useState<UITripRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<UITripRow | null>(null);
  const [tripDetails, setTripDetails] = useState<ApiTripDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [pendingDownloadTripId, setPendingDownloadTripId] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false); // reserved for future capture-specific styles
  const reportRef = useRef<HTMLDivElement>(null);
  // const mapRef = useRef<L.Map | null>(null);
  const [ridersByHourData, setRidersByHourData] = useState<any[]>([]);
  const [loadingRidersData, setLoadingRidersData] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null);
  const showNotice = (message: string, kind: 'success' | 'error' | 'info' = 'info') => setNotice({ message, kind });

  useEffect(() => {
    let isMounted = true;
    const fetchTrips = async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/admin/trips?page=1&limit=100`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });
  const json: ApiResponse<ApiTrip[]> = await res.json();
        // Debug: log trips fetched from backend API as-is
        // console.log("[Admin] /admin/trips raw data:", json?.data);
        if (json.success && Array.isArray(json.data)) {
          // Build rows preferring backend-provided friendly names
          const uiRows: UITripRow[] = json.data.map((t) => {
            const startTimeText = formatTimeIST(t.startTime);
            const endTimeText = formatTimeIST(t.endTime ?? null);
            const durationVal = computeDurationMins(t.startTime, t.endTime ?? null);
            const durationMinsText = typeof durationVal === "number" ? String(durationVal) : "-";
            const startLocDisp = (t as any).startLocationName || t.startLocation;
            const endLocText = (t as any).endLocationName || t.endLocation || "-";
            const a = parseLatLng(t.startLocation);
            const b = parseLatLng(t.endLocation ?? undefined);
            const distanceVal = a && b ? haversineKm(a, b) : null;
            const distanceKmText = distanceVal == null ? "-" : (distanceVal.toFixed(2));
            return {
              id: t.id,
              state: t.state,
              serviceDateText: formatDate(t.serviceDate),
              driverName: t.driver?.name || "-",
              startLocation: startLocDisp,
              endLocationText: endLocText,
              distanceKmText,
              startTimeText,
              endTimeText,
              durationMinsText,
              rawStartLocation: t.startLocation ?? null,
              rawEndLocation: t.endLocation ?? null,
            };
          });
          if (isMounted) setRows(uiRows);
        } else {
          if (isMounted) setRows([]);
        }
      } catch (e) {
        if (isMounted) setRows([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (token) fetchTrips();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const parseLatLng = (loc?: string | null): { lat: number; lng: number } | null => {
    if (!loc) return null;
    const m = loc.trim().match(/^\s*(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)\s*$/);
    if (!m) return null;
    const lat = parseFloat(m[1]);
    const lng = parseFloat(m[2]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const haversineKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const toRad = (x: number) => (x * Math.PI) / 180;
    const R = 6371; // km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s1 =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(s1), Math.sqrt(1 - s1));
    const d = R * c;
    // Guard against tiny floating errors; show 0 for extremely small distances
    return d < 1e-6 ? 0 : d;
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });

  const formatTimeIST = (iso?: string | null) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const computeDurationMins = (startIso?: string, endIso?: string | null) => {
    if (!startIso || !endIso) return undefined;
    const diffMs = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) return undefined;
    return Math.round(diffMs / 60000);
  };

  const statusPill = (label: string, type: "primary" | "success" | "error") => {
    const bg: Record<typeof type, string> = {
      primary: "#14b8a6",
      success: "#10b981",
      error: "#ef4444",
    } as const;
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.25,
          borderRadius: 16,
          fontSize: 12,
          lineHeight: 1.4,
          fontWeight: 500,
          color: "#fff",
          backgroundColor: bg[type],
        }}
      >
        {label}
      </Box>
    );
  };

  const getStatusChip = (state: string) => {
    const map: Record<string, { type: "primary" | "success" | "error"; label: string }> = {
      ongoing: { type: "primary", label: "Ongoing" },
      completed: { type: "success", label: "Completed" },
      cancelled: { type: "error", label: "Cancelled" },
    };
    const cfg = map[state] || { type: "primary", label: state } as const;
    return statusPill(cfg.label, cfg.type);
  };

  const getRiderStatusChip = (status: string) => {
    const map: Record<string, { type: "primary" | "success" | "error"; label: string }> = {
      joined: { type: "primary", label: "Joined" },
      completed: { type: "success", label: "Completed" },
      cancelled: { type: "error", label: "Cancelled" },
    };
    const cfg = map[status] || { type: "primary", label: status } as const;
    return statusPill(cfg.label, cfg.type);
  };

  const generatePdfFromDialog = useCallback(async () => {
    const el = reportRef.current;
    if (!el) return;
    setIsCapturing(true);
    // ensure map has proper size and settled transforms
    // Map sizing handled internally by TripPathMap; no external invalidate needed now.
    await new Promise((r) => setTimeout(r, 300));
    const canvas = await html2canvas(el, {
      useCORS: true,
      backgroundColor: "#ffffff",
      scale: 1,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = margin;
    pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - margin * 2;
    while (heightLeft > 0) {
      pdf.addPage();
      position = margin - (imgHeight - heightLeft);
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - margin * 2;
    }

    const driver = selectedTrip?.driverName?.replace(/[^a-z0-9]+/gi, "_") || "driver";
    const date = selectedTrip?.serviceDateText?.replace(/[^a-z0-9]+/gi, "_") || "date";
    pdf.save(`Trip_${date}_${driver}.pdf`);
    setIsCapturing(false);
  }, [selectedTrip]);

  const handleViewTrip = async (row: UITripRow) => {
    setSelectedTrip(row);
    setViewOpen(true);
    setDetailsLoading(true);
    setTripDetails(null);
    try {
      const res = await apiFetch(`/trips/${row.id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      const json: ApiTripDetailsResponse = await res.json();
      // Some backends might send shape { success, data: { ...trip } }
      const baseTrip: ApiTripDetails | undefined = json.trip || (json as any).data || undefined;
      if (json.success && baseTrip) {
        let details: ApiTripDetails = baseTrip;
        // Fetch full location logs for complete path plotting
        try {
          const logsRes = await apiFetch(`/trips/${row.id}/location-logs`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
          });
          const logsJson: ApiLocationLogsResponse = await logsRes.json();
          if (logsJson.success && Array.isArray(logsJson.logs)) {
            details = { ...details, locationLogs: logsJson.logs };
          }
        } catch {}
        // Ensure userTrips array exists for rider listing
        if (!details.userTrips) {
          details = { ...details, userTrips: [] };
        }
        // Optionally fetch enriched rider locations (boarding/alighting names) if an admin endpoint exists
        // Placeholder for future enhancement.
        setTripDetails(details);
      } else {
        setTripDetails(null);
      }
    } catch (e) {
      setTripDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDownloadReport = async (row: UITripRow) => {
    // If dialog already open for this trip and details loaded, generate directly
    if (viewOpen && selectedTrip?.id === row.id && !detailsLoading) {
      await generatePdfFromDialog();
      return;
    }
    setPendingDownloadTripId(row.id);
    await handleViewTrip(row);
  };

  const handleDownloadTripReport = async () => {
    try {
      setLoading(true);
      
      // Build query params based on current filters
      const params = new URLSearchParams();
      if (statusFilter !== "all") {
        params.append("state", statusFilter);
      }
      
      const res = await apiFetch(`/admin/trips/report?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      
      const json = await res.json();
      
      if (!json.success || !json.data) {
        showNotice("Failed to fetch trip report data", 'error');
        return;
      }
      
      const reportData = json.data;
      
      // Create Excel workbook
      const wb = XLSX.utils.book_new();
      
      // Sheet 1: Summary Statistics
      const summaryData = [
        ["Trip Report - Summary"],
        ["Generated on:", new Date().toLocaleString()],
        [""],
        ["Overall Statistics"],
        ["Total Trips", reportData.summary.totalTrips],
        ["Completed Trips", reportData.summary.completedTrips],
        ["Ongoing Trips", reportData.summary.ongoingTrips],
        ["Cancelled Trips", reportData.summary.cancelledTrips],
        ["Completion Rate (%)", `${reportData.summary.completionRate}%`],
        ["Average Duration (minutes)", reportData.summary.averageDurationMins],
        ["Average Occupancy (%)", `${reportData.summary.averageOccupancy}%`],
        [""],
        ["Time Period Analysis"],
        ["First Trip Date", reportData.summary.firstTripDate ? new Date(reportData.summary.firstTripDate).toLocaleDateString() : "N/A"],
        ["Last Trip Date", reportData.summary.lastTripDate ? new Date(reportData.summary.lastTripDate).toLocaleDateString() : "N/A"],
        ["Total Active Days", reportData.summary.totalActiveDays],
        ["Average Trips per Day", reportData.summary.averageTripsPerDay],
      ];
      
      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, ws1, "Summary");
      
      // Sheet 2: Trips by Month
      const monthData = [
        ["Month", "Number of Trips"],
        ...reportData.timeAnalysis.tripsByMonth.map((item: any) => [
          item.month,
          item.count,
        ]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(monthData);
      XLSX.utils.book_append_sheet(wb, ws2, "Trips by Month");
      
      // Sheet 3: Trips by Week
      const weekData = [
        ["Week", "Number of Trips"],
        ...reportData.timeAnalysis.tripsByWeek.map((item: any) => [
          item.week,
          item.count,
        ]),
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(weekData);
      XLSX.utils.book_append_sheet(wb, ws3, "Trips by Week");
      
      // Sheet 4: Trips by Day of Week
      const dayOfWeekData = [
        ["Day of Week", "Number of Trips"],
        ...Object.entries(reportData.timeAnalysis.tripsByDayOfWeek).map(([day, count]) => [
          day,
          count,
        ]),
      ];
      const ws4 = XLSX.utils.aoa_to_sheet(dayOfWeekData);
      XLSX.utils.book_append_sheet(wb, ws4, "Trips by Day of Week");
      
      // Sheet 5: Trips by Hour
      const hourData = [
        ["Hour", "Time Range", "Number of Trips"],
        ...reportData.timeAnalysis.tripsByHour
          .sort((a: any, b: any) => a.hour - b.hour)
          .map((item: any) => [
            item.hour,
            item.timeRange,
            item.count,
          ]),
      ];
      const ws5 = XLSX.utils.aoa_to_sheet(hourData);
      XLSX.utils.book_append_sheet(wb, ws5, "Trips by Hour");

      // Sheet 5b: Riders by Hour
      const ridersByHourData = [
        ["Hour", "Time Range", "Number of Riders"],
        ...(reportData.timeAnalysis.ridersByHour || [])
          .sort((a: any, b: any) => a.hour - b.hour)
          .map((item: any) => [
            item.hour,
            item.timeRange,
            item.count,
          ]),
      ];
      const ws5b = XLSX.utils.aoa_to_sheet(ridersByHourData);
      XLSX.utils.book_append_sheet(wb, ws5b, "Riders by Hour");
      
      // Sheet 6: Peak Hours
      const peakHoursData = [
        ["Rank", "Hour", "Time Range", "Number of Trips"],
        ...reportData.timeAnalysis.peakHours.map((item: any, index: number) => [
          index + 1,
          item.hour,
          item.timeRange,
          item.count,
        ]),
      ];
      const ws6 = XLSX.utils.aoa_to_sheet(peakHoursData);
      XLSX.utils.book_append_sheet(wb, ws6, "Peak Hours");
      
      // Sheet 7: Detailed Hourly Breakdown by Date
      const hourlyBreakdownData: any[] = [
        ["Date", "Day of Week", "Hour", "Time Range", "Number of Trips"],
      ];
      
      if (reportData.timeAnalysis.hourlyBreakdown && reportData.timeAnalysis.hourlyBreakdown.length > 0) {
        reportData.timeAnalysis.hourlyBreakdown.forEach((dayData: any) => {
          dayData.hours.forEach((hourData: any) => {
            if (hourData.count > 0) {
              hourlyBreakdownData.push([
                dayData.date,
                dayData.dayOfWeek,
                hourData.hour,
                hourData.timeRange,
                hourData.count,
              ]);
            }
          });
        });
      }
      
      const ws7 = XLSX.utils.aoa_to_sheet(hourlyBreakdownData);
      XLSX.utils.book_append_sheet(wb, ws7, "Hourly by Date");

      // Sheet 7b: Hourly Breakdown Matrix (Date x Hour pivot style)
      const hourlyMatrixData: any[] = [];
      if (reportData.timeAnalysis.hourlyBreakdown && reportData.timeAnalysis.hourlyBreakdown.length > 0) {
        // Create header row with hours
        const headerRow = ["Date", "Day of Week", "Total"];
        for (let h = 0; h < 24; h++) {
          headerRow.push(`${h}:00`);
        }
        hourlyMatrixData.push(headerRow);

        // Add data rows for each date
        reportData.timeAnalysis.hourlyBreakdown.forEach((dayData: any) => {
          const row: any[] = [
            dayData.date,
            dayData.dayOfWeek,
            dayData.totalTrips,
          ];
          // Add trip count for each hour (0-23)
          for (let h = 0; h < 24; h++) {
            const hourData = dayData.hours.find((hr: any) => hr.hour === h);
            row.push(hourData ? hourData.count : 0);
          }
          hourlyMatrixData.push(row);
        });
      }
      
      if (hourlyMatrixData.length > 0) {
        const ws7b = XLSX.utils.aoa_to_sheet(hourlyMatrixData);
        XLSX.utils.book_append_sheet(wb, ws7b, "Hourly Matrix");
      }

      // Sheet 8: Detailed Trip Data
      const tripDetailsData = [
        [
          "Trip ID",
          "Service Date",
          "Start Time",
          "End Time",
          "Duration (min)",
          "Driver Name",
          "Driver Email",
          "Cab Plate",
          "Cab Capacity",
          "Rider Count",
          "Occupancy (%)",
          "Start Location",
          "End Location",
          "State",
        ],
        ...reportData.trips.map((trip: any) => [
          trip.id,
          new Date(trip.serviceDate).toLocaleDateString(),
          trip.startTime ? new Date(trip.startTime).toLocaleTimeString() : "N/A",
          trip.endTime ? new Date(trip.endTime).toLocaleTimeString() : "N/A",
          trip.durationMins ?? "N/A",
          trip.driver.name,
          trip.driver.email,
          trip.cabPlate,
          trip.cabCapacity ?? "N/A",
          trip.riderCount,
          `${trip.occupancy}%`,
          trip.startLocationName ||  trip.startLocation,
          trip.endLocationName || trip.endLocation || "N/A",
          trip.state,
        ]),
      ];
      const ws8 = XLSX.utils.aoa_to_sheet(tripDetailsData);
      XLSX.utils.book_append_sheet(wb, ws8, "All Trips");
      
      // Generate filename with date range
      const filename = `trip-report-${new Date().toISOString().split("T")[0]}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      
      showNotice("Trip report downloaded successfully!", 'success');
    } catch (error) {
      console.error("Error downloading trip report:", error);
      showNotice("Failed to download trip report. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate PDF once details are loaded and dialog is open
  useEffect(() => {
    if (
      pendingDownloadTripId &&
      viewOpen &&
      !detailsLoading &&
      selectedTrip?.id === pendingDownloadTripId
    ) {
      const t = setTimeout(async () => {
        try {
          await generatePdfFromDialog();
        } finally {
          setPendingDownloadTripId(null);
        }
      }, 600);
      return () => clearTimeout(t);
    }
  }, [pendingDownloadTripId, viewOpen, detailsLoading, selectedTrip, generatePdfFromDialog]);

  // FitMapBounds no longer needed with TripPathMap

  const getTripPathPositions = (): [number, number][] => {
    const coords: [number, number][] = [];
    // Prefer tripDetails raw locations; fall back to raw coords stored in selectedTrip
    const startStr = (tripDetails?.startLocation ?? selectedTrip?.rawStartLocation) || null;
    const endStr = (tripDetails?.endLocation ?? selectedTrip?.rawEndLocation) || null;

    const start = parseLatLng(startStr);
    if (start) coords.push([start.lat, start.lng]);

    const logs = (tripDetails?.locationLogs ?? [])
      .slice()
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    for (const l of logs) {
      const p = parseLatLng(l.location);
      if (p) coords.push([p.lat, p.lng]);
    }

    const end = parseLatLng(endStr ?? null);
    if (end) coords.push([end.lat, end.lng]);

    return coords;
  };

  const columns: GridColDef<UITripRow>[] = [
    { field: "serviceDateText", headerName: "Date", width: 100, flex: 0.6 },
    { field: "driverName", headerName: "Driver", width: 120, flex: 0.7 },
    {
      field: "state",
      headerName: "Status",
      width: 100,
      flex: 0.6,
      renderCell: (params) => getStatusChip(params.value as string),
    },
    { field: "startLocation", headerName: "Pickup", width: 200, flex: 1.2 },
    { field: "endLocationText", headerName: "Destination", width: 200, flex: 1.2 },
    { field: "distanceKmText", headerName: "Distance (km)", width: 120, flex: 0.7 },
    { field: "startTimeText", headerName: "Pickup Time", width: 130, flex: 0.8 },
    { field: "endTimeText", headerName: "Destination Time", width: 150, flex: 0.9 },
    { field: "durationMinsText", headerName: "Duration (min)", width: 120, flex: 0.7 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      flex: 0.7,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View Details"
          onClick={() => handleViewTrip((params as any).row as UITripRow)}
        />,
        <GridActionsCellItem
          icon={<DownloadIcon />}
          label="Download Report"
          onClick={() => handleDownloadReport((params as any).row as UITripRow)}
        />,
      ],
    },
  ];

  const filteredTrips = rows.filter((trip) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (trip.driverName || "").toLowerCase().includes(term) ||
      (trip.startLocation || "").toLowerCase().includes(term) ||
      (trip.endLocationText || "").toLowerCase().includes(term);

    const matchesStatus = statusFilter === "all" || trip.state === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <Box>
      {notice && (
        <Notification
          kind={notice.kind}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}
      {/* Header Section with Gradient Background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
          color: 'white',
          p: 4,
          borderRadius: 2,
          mb: 3,
          boxShadow: '0 8px 32px rgba(20, 184, 166, 0.3)',
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Trip Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Monitor and analyze trip data and performance
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<ReportIcon />}
            onClick={handleDownloadTripReport}
            disabled={loading}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            Download Trip Report
          </Button>
        </Box>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%)',
          border: '1px solid #10b98130',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Search trips..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="ongoing">Ongoing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ height: 600, width: "100%", overflow: 'hidden' }}>
        <DataGrid
          rows={filteredTrips}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [
                { field: "serviceDateText", sort: "desc" },
              ],
            },
          }}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiDataGrid-columnHeader': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Paper>

      {/* Riders by Hour Analysis Section */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          mt: 2,
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%)',
          border: '1px solid #10b98130',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Riders by Hour Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track peak hours based on number of riders traveling
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={async () => {
              setLoadingRidersData(true);
              try {
                const params = new URLSearchParams();
                if (statusFilter !== "all") {
                  params.append("state", statusFilter);
                }
                const res = await apiFetch(`/admin/trips/report?${params.toString()}`, {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: token ? `Bearer ${token}` : "",
                  },
                });
                const json = await res.json();
                if (json.success && json.data?.timeAnalysis?.ridersByHour) {
                  const sorted = json.data.timeAnalysis.ridersByHour
                    .sort((a: any, b: any) => a.hour - b.hour);
                  setRidersByHourData(sorted);
                }
              } catch (error) {
                console.error("Error fetching riders data:", error);
              } finally {
                setLoadingRidersData(false);
              }
            }}
            disabled={loadingRidersData || !token}
          >
            {loadingRidersData ? <CircularProgress size={20} /> : "Load Analysis"}
          </Button>
        </Box>

        {ridersByHourData.length > 0 && (
          <>
            {/* Table */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Riders per Hour Interval
              </Typography>
              <Table size="small" sx={{ backgroundColor: 'white', borderRadius: 1 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#14b8a6', '& .MuiTableCell-head': { color: 'white', fontWeight: 600 } }}>
                    <TableCell>Hour</TableCell>
                    <TableCell>Time Interval</TableCell>
                    <TableCell align="right">Number of Riders</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ridersByHourData.map((item: any) => {
                    const total = ridersByHourData.reduce((sum: number, i: any) => sum + i.count, 0);
                    const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : "0";
                    const maxCount = Math.max(...ridersByHourData.map((i: any) => i.count));
                    return (
                      <TableRow 
                        key={item.hour}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#f9fafb' },
                          '&:hover': { backgroundColor: '#f3f4f6' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{item.hour}</TableCell>
                        <TableCell>{item.timeRange}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{item.count}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <Box sx={{ width: 60, height: 8, backgroundColor: '#e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
                              <Box
                                sx={{
                                  width: `${(item.count / maxCount) * 100}%`,
                                  height: '100%',
                                  backgroundColor: item.count === maxCount ? '#ef4444' : '#14b8a6',
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right' }}>
                              {percentage}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Bar Chart */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Visual Chart
              </Typography>
              <Box sx={{ backgroundColor: 'white', p: 3, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: 300, gap: 0.5 }}>
                  {ridersByHourData.map((item: any) => {
                    const maxCount = Math.max(...ridersByHourData.map((i: any) => i.count));
                    const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    const isPeak = item.count === maxCount;
                    return (
                      <Box key={item.hour} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'flex-end',
                            width: '100%',
                            maxWidth: '40px',
                            justifyContent: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              height: `${height}%`,
                              minHeight: item.count > 0 ? '4px' : '0',
                              backgroundColor: isPeak ? '#ef4444' : '#14b8a6',
                              borderRadius: '4px 4px 0 0',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              '&:hover': {
                                backgroundColor: isPeak ? '#dc2626' : '#0f766e',
                                transform: 'scaleY(1.05)',
                              },
                            }}
                            title={`${item.timeRange}: ${item.count} riders`}
                          >
                            {item.count > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  position: 'absolute',
                                  top: -20,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  fontWeight: 600,
                                  color: '#374151',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.count}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            fontSize: '0.7rem',
                            color: '#6b7280',
                            fontWeight: item.hour % 4 === 0 ? 600 : 400,
                          }}
                        >
                          {item.hour}h
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: '#14b8a6', borderRadius: 1 }} />
                    <Typography variant="caption">Regular Hours</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: '#ef4444', borderRadius: 1 }} />
                    <Typography variant="caption">Peak Hour</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {ridersByHourData.length === 0 && !loadingRidersData && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Click "Load Analysis" to view riders per hour breakdown
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Trip Details Dialog */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Trip Details
          <IconButton
            aria-label="close"
            onClick={() => setViewOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTrip && (
            <Box
              ref={reportRef}
              sx={{ backgroundColor: "#fff" }}
              className={isCapturing ? "pdf-capturing" : undefined}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Basic details
              </Typography>
              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                gap: 2,
                mb: 2,
              }}>
                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Driver</Typography>
                    <Typography variant="body1">{selectedTrip.driverName}</Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box>{getStatusChip(selectedTrip.state)}</Box>
                  </Stack>
                </Box>
                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body1">{selectedTrip.serviceDateText}</Typography>
                  </Stack>
                </Box>

                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Pickup</Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{selectedTrip.startLocation}</Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Destination</Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{selectedTrip.endLocationText}</Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Distance (km)</Typography>
                    <Typography variant="body1">{selectedTrip.distanceKmText}</Typography>
                  </Stack>
                </Box>

                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Pickup Time</Typography>
                    <Typography variant="body1">{selectedTrip.startTimeText}</Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Destination Time</Typography>
                    <Typography variant="body1">{selectedTrip.endTimeText}</Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Duration (min)</Typography>
                    <Typography variant="body1">{selectedTrip.durationMinsText}</Typography>
                  </Stack>
                </Box>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Trip path
              </Typography>
              <Box sx={{ height: 320, width: "100%", mb: 2 }}>
                <TripPathMap
                  key={selectedTrip?.id || "trip-map"}
                  coords={getTripPathPositions()}
                  height={320}
                  endIconUrl={(tripDetails?.state || selectedTrip?.state) === 'ongoing' ? '/markers/taxi-marker.svg' : undefined}
                  endIconSize={(tripDetails?.state || selectedTrip?.state) === 'ongoing' ? [36, 36] as [number, number] : undefined}
                />
              </Box>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Riders
              </Typography>

              {detailsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : tripDetails && tripDetails.userTrips && tripDetails.userTrips.length > 0 ? (
                <Table size="small" aria-label="riders table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rider</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Boarding Location</TableCell>
                      <TableCell>Alighting Location</TableCell>
                      <TableCell>Distance (km)</TableCell>
                      <TableCell>Boarding Time</TableCell>
                      <TableCell>Alighting Time</TableCell>
                      <TableCell>Duration (min)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tripDetails.userTrips.map((ut, idx) => {
                      const distVal = (() => {
                        const a = parseLatLng(ut.boardingLocation ?? null);
                        const b = parseLatLng(ut.alightingLocation ?? null);
                        if (!a || !b) return null;
                        return haversineKm(a, b);
                      })();
                      const distText = distVal == null ? "-" : distVal.toFixed(2);
                      const durVal = computeDurationMins(ut.boardingTime ?? undefined, ut.alightingTime ?? undefined);
                      const durText = typeof durVal === "number" ? String(durVal) : "-";
                      return (
                        <TableRow key={idx}>
                          <TableCell>{ut.user?.name ?? "-"}</TableCell>
                          <TableCell>{getRiderStatusChip(ut.status)}</TableCell>
                          <TableCell sx={{ wordBreak: "break-all" }}>{(ut as any).boardingLocationName || ut.boardingLocation || "-"}</TableCell>
                          <TableCell sx={{ wordBreak: "break-all" }}>{(ut as any).alightingLocationName || ut.alightingLocation || "-"}</TableCell>
                          <TableCell>{distText}</TableCell>
                          <TableCell>{formatTimeIST(ut.boardingTime ?? null)}</TableCell>
                          <TableCell>{formatTimeIST(ut.alightingTime ?? null)}</TableCell>
                          <TableCell>{durText}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No riders for this trip.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Trips;
